import React, {useCallback, useContext, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack
} from '@mui/material';
import {useLocation, useNavigate} from "react-router";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HistoryIcon from '@mui/icons-material/History';
import {AuthContext, useStickyState} from "@lb/mlib-components";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import StraightenIcon from '@mui/icons-material/Straighten';
import FaceIcon from '@mui/icons-material/Face';
import PrintIcon from '@mui/icons-material/Print';
import SubjectIcon from '@mui/icons-material/Subject';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';
import {
    Balance,
    CardGiftcard,
    FileUpload,
    Healing,
    HowToReg,
    LocalMallOutlined,
    LocalShipping
} from "@mui/icons-material";
import SsidChartIcon from '@mui/icons-material/SsidChart';
import LocalShippingTwoToneIcon from '@mui/icons-material/LocalShippingTwoTone';
import {titleCase} from "../../utils/titleCase";
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EventIcon from '@mui/icons-material/Event';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import axios from "axios";

const listIconMinWidth = 40;

export default function PagesMenu() {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const {build} = window['runConfig'];

    const [expandedAudit, setExpandedAudit] = useStickyState(false, 'ziggyExpandedAudit');
    const [expandedProduction, setExpandedProduction] = useStickyState(false, 'ziggyExpandedProduction');
    const [expandedReporting, setExpandedReporting] = useStickyState(false, 'ziggyExpandedReporting');
    const [expandedShipping, setExpandedShipping] = useStickyState(false, 'ziggyExpandedShipping');
    const [expandedSettings, setExpandedSettings] = useStickyState(false, 'ziggyExpandedSettings');

    const [downloadingQuarantine, setDownloadingQuarantine] = useState(false);

    const handleExpandAudit = useCallback(() => setExpandedAudit(prev => !prev), [setExpandedAudit])
    const handleExpandProduction = useCallback(() => setExpandedProduction(prev => !prev), [setExpandedProduction])
    const handleExpandReporting = useCallback(() => setExpandedReporting(prev => !prev), [setExpandedReporting])
    const handleExpandShipping = useCallback(() => setExpandedShipping(prev => !prev), [setExpandedShipping])
    const handleExpandSettings = useCallback(() => setExpandedSettings(prev => !prev), [setExpandedSettings])

    const handleQuarantineDownload = useCallback(async (e) => {
        e.stopPropagation();
        setDownloadingQuarantine(true);

        axios.get(`tariffs/tracking/alloy-lot-work`, {
            responseType: 'blob'
        }).then((response) => {
            const cd =
                response.headers?.["content-disposition"] ||
                response.headers?.["Content-Disposition"];

            const match = cd?.match(/filename=([^;]+)/i);
            const filename = match
                ? match[1].replace(/"/g, "").trim()
                : `Unknown_Lots.xlsx`;

            const urlObject = window.URL || window.webkitURL;
            const downloadUrl = urlObject.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                urlObject.revokeObjectURL(downloadUrl);
            }, 100);
        }).finally(() => {
            setDownloadingQuarantine(false);
        });
    }, []);

    function MyListButton({path, title, children, beta, onDownload, downloading, disableNav, matchPrefix}) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
                <ListItemButton
                    selected={matchPrefix ? location.pathname.startsWith(matchPrefix) : location.pathname === path}
                    onClick={disableNav ? undefined : () => navigate(path)}
                    disabled={disableNav}
                    sx={{
                        flexGrow: 1, flexShrink: 1, minWidth: 0, ...(disableNav && {
                            '&.Mui-disabled': {
                                opacity: 1,
                                pointerEvents: 'none'
                            }
                        })
                    }}
                >
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        {children}
                    </ListItemIcon>
                    <Stack direction={'row'} spacing={1} sx={{
                        alignItems: 'center'
                    }}>
                        <ListItemText primary={title || titleCase(path.slice(1, path.length))}/>
                        {beta && <Chip label={'BETA'} size={"small"} variant={"outlined"}/>}
                    </Stack>
                </ListItemButton>
                {onDownload && (
                    <IconButton
                        size="small"
                        onClick={onDownload}
                        disabled={downloading}
                        sx={{mr: 1, ml: 'auto', flexShrink: 0}}
                    >
                        {downloading
                            ? <CircularProgress size={16}/>
                            : <DownloadIcon fontSize="small"/>
                        }
                    </IconButton>
                )}
            </Box>
        );
    }

    return (
        <>
            <Accordion square disableGutters elevation={0}
                       expanded={expandedShipping} onChange={handleExpandShipping}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                  aria-controls="panel1a-content"
                                  id="panel1a-header">
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        <LocalShippingIcon/>
                    </ListItemIcon>
                    <ListItemText primary="Shipping"/>
                </AccordionSummary>

                <Divider/>

                <AccordionDetails>
                    <MyListButton path='/shipping/prepare' title='Prepare'>
                        <ShoppingCartIcon/>
                    </MyListButton>
                    <MyListButton path='/shipping/fulfilment' title='Fulfilment'>
                        <SubjectIcon/>
                    </MyListButton>
                    <MyListButton path='/shipping/scheduler' title='Scheduler'>
                        <DateRangeIcon/>
                    </MyListButton>
                    <MyListButton path='/shipping/forpickup' title='For Pickup'>
                        <ShoppingBagIcon/>
                    </MyListButton>
                    <MyListButton path='/shipping/transit' title='Transit'>
                        <LocalShippingTwoToneIcon/>
                    </MyListButton>
                    <Divider/>
                    <MyListButton path='/shipping/history' title='History'>
                        <HistoryIcon/>
                    </MyListButton>
                </AccordionDetails>
            </Accordion>

            {expandedProduction && <Divider/>}

            <Accordion square disableGutters elevation={0}
                       expanded={expandedProduction} onChange={handleExpandProduction}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                  aria-controls="panel1a-content"
                                  id="panel1a-header">
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        <PrecisionManufacturingIcon/>
                    </ListItemIcon>
                    <ListItemText primary="Production"/>
                </AccordionSummary>

                <Divider/>

                <AccordionDetails>
                    <MyListButton path={'/production'} title={'Orders'}>
                        <ShoppingCartOutlinedIcon/>
                    </MyListButton>
                    <MyListButton path={'/production/stock'} title={'Stock'}>
                        <InventoryOutlinedIcon/>
                    </MyListButton>
                    <MyListButton title={'Reconcile'} path={'/production/reconcile'}>
                        <CleaningServicesIcon/>
                    </MyListButton>
                    <MyListButton beta path={'/production/quality'} title={'Quality'}>
                        <AutoGraphIcon/>
                    </MyListButton>
                    <MyListButton path={'/production/qchold'} title={'QC Hold'}>
                        <HowToReg/>
                    </MyListButton>
                </AccordionDetails>
            </Accordion>

            {expandedReporting && <Divider/>}

            <Accordion square disableGutters elevation={0}
                       expanded={expandedReporting} onChange={handleExpandReporting}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                  aria-controls="panel1a-content"
                                  id="panel1a-header">
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        <AssessmentIcon/>
                    </ListItemIcon>
                    <ListItemText primary="Reporting"/>
                </AccordionSummary>

                <Divider/>

                <AccordionDetails>
                    <MyListButton path='/reporting/finance' title='Finance'>
                        <SsidChartIcon/>
                    </MyListButton>
                    <MyListButton path='/reporting/logistics' title='Logistics'>
                        <LocalShipping/>
                    </MyListButton>
                    <MyListButton path='/reporting/sampleorders' title='Sample Orders'>
                        <LocalMallOutlined/>
                    </MyListButton>
                </AccordionDetails>
            </Accordion>

            {expandedAudit && <Divider/>}

            <Accordion square disableGutters elevation={0} expanded={expandedAudit}
                       onChange={handleExpandAudit}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                  aria-controls="panel1a-content"
                                  id="panel1a-header">
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        <FactCheckIcon/>
                    </ListItemIcon>
                    <ListItemText primary="Audit"/>
                </AccordionSummary>

                <Divider/>

                <AccordionDetails>
                    <MyListButton path={'/audit/salesorders'} title={'Sales Orders'}>
                        <ShoppingCartOutlinedIcon/>
                    </MyListButton>
                    <MyListButton path={'/audit/shipments'} title={'Shipment'}>
                        <CardGiftcard/>
                    </MyListButton>
                    <MyListButton path={'/audit/warehouse'} title={'Warehouse'}>
                        <WarehouseOutlinedIcon/>
                    </MyListButton>
                    <MyListButton path={'/audit/lbship/expected'} matchPrefix={'/audit/lbship'} title={'LB Ship'}>
                        <LocalShippingTwoToneIcon/>
                    </MyListButton>
                    <MyListButton
                        path={'/audit/quarantinelots'}
                        title={'Lot Analysis'}
                        onDownload={handleQuarantineDownload}
                        downloading={downloadingQuarantine}
                        disableNav
                    >
                        <Healing/>
                    </MyListButton>
                    {build === "Development" &&
                        <MyListButton path={'/audit/clone'} title={'Clone'}>
                            <ContentCopyIcon/>
                        </MyListButton>}
                    {authCtx.hasRole('audit_tariffs') &&
                        <MyListButton path='/audit/alloytariffs' title='Alloy Tariffs'>
                            <Balance/>
                        </MyListButton>}
                    {authCtx.hasRole('audit_tariffs') &&
                        <MyListButton path='/audit/fileupload' title='Tariff Uploads'>
                            <FileUpload/>
                        </MyListButton>}
                </AccordionDetails>
            </Accordion>

            {expandedSettings && <Divider/>}

            <Accordion square disableGutters elevation={0} expanded={expandedSettings}
                       onChange={handleExpandSettings}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                  aria-controls="panel1a-content"
                                  id="panel1a-header">
                    <ListItemIcon sx={{minWidth: listIconMinWidth}}>
                        <SettingsIcon/>
                    </ListItemIcon>
                    <ListItemText primary="Settings"/>
                </AccordionSummary>

                <Divider/>

                <AccordionDetails>
                    <List>
                        <MyListButton path='/settings/couriers' title='Couriers'>
                            <LocalShippingOutlinedIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/dims' title='Dims'>
                            <StraightenIcon/>
                        </MyListButton>
                        {build === 'Development' &&
                            <MyListButton path='/settings/freeitems' matchPrefix='/settings/freeitems' title={'Free Items'}>
                                <CardGiftcard/>
                            </MyListButton>}
                        <MyListButton path='/settings/holidays' title='Holidays'>
                            <EventIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/pricecuts' title={'Price Cuts'}>
                            <AttachMoneyIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/printers' title='Printers'>
                            <PrintIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/stations' matchPrefix='/settings/stations' title='Stations'>
                            <FaceIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/targets' title='Targets'>
                            <CrisisAlertIcon/>
                        </MyListButton>
                        <MyListButton path='/settings/trackers' title='Trackers'>
                            <VideoLabelIcon/>
                        </MyListButton>
                        {build === "Development" &&
                            <MyListButton path={'/testorders'} title={'Test Orders'}>
                                <AddShoppingCartIcon/>
                            </MyListButton>}
                    </List>
                </AccordionDetails>
            </Accordion>

            <Divider/>
        </>
    );
}
